import axios from "axios";

// Use same-origin API calls when the app is served from a non-Emergent host
// (e.g. the user's custom domain like `breakoutscanner.co.in`). This is what
// makes httpOnly auth cookies actually work in production — cookies set by the
// backend are scoped to the host that served them, so a cross-origin call from
// `breakoutscanner.co.in` → `breakout-scanner-14.preview.emergentagent.com`
// would silently drop the session cookie and break login.
//
// Behaviour:
//   - On localhost / *.preview.emergentagent.com / *.emergent.host:
//     fall back to `REACT_APP_BACKEND_URL` (preview-time configuration).
//   - On every other host (custom domains): call same-origin.
function resolveBackendUrl() {
  const envUrl = process.env.REACT_APP_BACKEND_URL || "";
  if (typeof window === "undefined") return envUrl;
  const host = window.location.hostname || "";
  const isEmergentHost =
    host === "localhost" ||
    host === "127.0.0.1" ||
    host.endsWith(".preview.emergentagent.com") ||
    host.endsWith(".emergent.host") ||
    host.endsWith(".emergentagent.com");
  if (isEmergentHost) return envUrl;
  return window.location.origin;
}

const BACKEND_URL = resolveBackendUrl();
export const API_BASE = `${BACKEND_URL}/api`;

// Auth is delivered via secure httpOnly cookies set by the backend.
// `withCredentials: true` ensures the browser sends those cookies on every request.
export const api = axios.create({
  baseURL: API_BASE,
  withCredentials: true,
});

// One-time migration: purge any legacy JWT that older builds stashed in localStorage.
// httpOnly cookies are now the single source of truth — keeping a copy in JS-readable
// storage would defeat the XSS hardening.
try {
  if (typeof window !== "undefined" && window.localStorage?.getItem("access_token")) {
    window.localStorage.removeItem("access_token");
  }
} catch (_) {
  /* storage disabled — ignore */
}

// Exported so other API helpers (public endpoints, OAuth bouncers, etc.) can
// stay in lock-step with the auth client.
export const BACKEND_BASE_URL = BACKEND_URL;

export function formatApiError(detail) {
  if (detail == null) return "Something went wrong";
  if (typeof detail === "string") return detail;
  if (Array.isArray(detail))
    return detail
      .map((e) => (e && typeof e.msg === "string" ? e.msg : JSON.stringify(e)))
      .join(" ");
  if (detail?.msg) return detail.msg;
  return String(detail);
}
